type JsonRecord = Record<string, unknown>;

export type InferredProjectPerson = {
	ari?: string;
	avatarUrl?: string;
	jobTitle?: string;
	name: string;
};

export type InferredProjectSource = {
	id?: string;
	title?: string;
	url: string;
};

export type InferredProjectContext = {
	collaborators: InferredProjectPerson[];
	owners: InferredProjectPerson[];
	sources: InferredProjectSource[];
	summary?: string;
	title?: string;
};

const isRecord = (value: unknown): value is JsonRecord =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const decodeJson = (value: unknown): unknown => {
	let decoded = value;

	for (let attempt = 0; attempt < 2 && typeof decoded === 'string'; attempt++) {
		try {
			decoded = JSON.parse(decoded);
		} catch {
			break;
		}
	}

	return decoded;
};

const getString = (record: JsonRecord, key: string): string | undefined => {
	const value = record[key];
	return typeof value === 'string' && value.trim() ? value : undefined;
};

const findDetailsEntities = (value: unknown, depth = 0): JsonRecord[] | undefined => {
	if (depth > 6) {
		return undefined;
	}

	const decoded = decodeJson(value);
	if (Array.isArray(decoded)) {
		for (const item of decoded) {
			const detailsEntities = findDetailsEntities(item, depth + 1);
			if (detailsEntities) {
				return detailsEntities;
			}
		}
		return undefined;
	}

	if (!isRecord(decoded)) {
		return undefined;
	}

	const facets = decoded.facets;
	if (isRecord(facets) && isRecord(facets.details) && Array.isArray(facets.details.entities)) {
		return facets.details.entities.filter(isRecord);
	}

	for (const nestedValue of Object.values(decoded)) {
		const detailsEntities = findDetailsEntities(nestedValue, depth + 1);
		if (detailsEntities) {
			return detailsEntities;
		}
	}

	return undefined;
};

const getEntityData = (detailsEntities: readonly JsonRecord[], key: string): JsonRecord[] => {
	const detail = detailsEntities.find((entity) => entity.key === key);
	if (!detail || !isRecord(detail.value) || !Array.isArray(detail.value.nodes)) {
		return [];
	}

	return detail.value.nodes.flatMap((node) =>
		isRecord(node) && isRecord(node.data) ? [node.data] : [],
	);
};

type InferredProjectEntityNode = { data: JsonRecord; id?: string };

const getEntityNodes = (
	detailsEntities: readonly JsonRecord[],
	key: string,
): InferredProjectEntityNode[] => {
	const detail = detailsEntities.find((entity) => entity.key === key);
	if (!detail || !isRecord(detail.value) || !Array.isArray(detail.value.nodes)) {
		return [];
	}

	return detail.value.nodes.flatMap((node) => {
		if (!isRecord(node) || !isRecord(node.data)) {
			return [];
		}
		const id = getString(node, 'id');
		return [{ data: node.data, ...(id ? { id } : {}) }];
	});
};

const parsePeople = (data: readonly JsonRecord[]): InferredProjectPerson[] =>
	data.flatMap((person) => {
		const name = getString(person, 'name');
		if (!name) {
			return [];
		}

		const extendedProfile = isRecord(person.extendedProfile) ? person.extendedProfile : undefined;
		const jobTitle = extendedProfile ? getString(extendedProfile, 'jobTitle') : undefined;
		const ari = getString(person, 'ari');
		const avatarUrl = getString(person, 'avatarUrl');
		return [
			{
				name,
				...(ari ? { ari } : {}),
				...(avatarUrl ? { avatarUrl } : {}),
				...(jobTitle ? { jobTitle } : {}),
			},
		];
	});

const parseReferenceIssues = (
	nodes: readonly InferredProjectEntityNode[],
): InferredProjectSource[] =>
	nodes.flatMap(({ data: issue, id }) => {
		const url = getString(issue, 'webUrl');
		if (!url) {
			return [];
		}

		const title = getString(issue, 'summary') ?? getString(issue, 'key');
		return [{ url, ...(id ? { id } : {}), ...(title ? { title } : {}) }];
	});

const parseReferencePages = (
	nodes: readonly InferredProjectEntityNode[],
): InferredProjectSource[] =>
	nodes.flatMap(({ data: page, id }) => {
		const pageLinks = isRecord(page.pageLinks) ? page.pageLinks : undefined;
		const base = pageLinks ? getString(pageLinks, 'base') : undefined;
		const webUi = pageLinks ? getString(pageLinks, 'webUi') : undefined;
		if (!base || !webUi) {
			return [];
		}

		const title = getString(page, 'pageName');
		return [{ url: `${base}${webUi}`, ...(id ? { id } : {}), ...(title ? { title } : {}) }];
	});

const parseInferredProjectMetadata = (
	detailsEntities: readonly JsonRecord[],
): Pick<InferredProjectContext, 'summary' | 'title'> => {
	const inferredProject = detailsEntities.find((entity) => entity.key === 'inferredProject');
	if (
		!inferredProject ||
		!isRecord(inferredProject.value) ||
		!isRecord(inferredProject.value.data)
	) {
		return {};
	}

	const metadata = inferredProject.value.data.metadata;
	if (!isRecord(metadata)) {
		return {};
	}

	const title = getString(metadata, 'title');
	const summary = getString(metadata, 'summary');
	return { ...(title ? { title } : {}), ...(summary ? { summary } : {}) };
};

export const parseInferredProjectContext = (outputs: unknown): InferredProjectContext => {
	const detailsEntities = findDetailsEntities(outputs) ?? [];
	const metadata = parseInferredProjectMetadata(detailsEntities);

	return {
		collaborators: parsePeople(getEntityData(detailsEntities, 'collaborators')),
		owners: parsePeople(getEntityData(detailsEntities, 'owners')),
		sources: [
			...parseReferenceIssues(getEntityNodes(detailsEntities, 'referenceIssues')),
			...parseReferencePages(getEntityNodes(detailsEntities, 'referencePages')),
		],
		...metadata,
	};
};
